import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import EmptyState from '../components/EmptyState';
import InlineAlert from '../components/InlineAlert';
import PageHeader from '../components/PageHeader';
import { fetchSalesforceAccountById } from '../services/salesforceService';

const formatDateTime = (value) => {
  if (!value) return 'Unavailable';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Unavailable';

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(parsed);
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return 'Unavailable';
  }

  return value;
};

const normalizeWebsiteUrl = (value) => {
  if (!value) return null;
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
};

const normalizeAbsoluteHttpUrl = (value) => {
  if (typeof value !== 'string') return null;
  const trimmedValue = value.trim();
  return /^https?:\/\//i.test(trimmedValue) ? trimmedValue : null;
};

const toAutomationKey = (value) => String(value || 'unavailable')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/(^-|-$)/g, '') || 'unavailable';

const AccountDetails = () => {
  const { accountId } = useParams();
  const [account, setAccount] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const loadAccount = useCallback(async () => {
    if (!accountId) {
      setError('Account ID is missing from the route.');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const accountResult = await fetchSalesforceAccountById(accountId);
      setAccount(accountResult?.account || null);
    } catch (requestError) {
      setError(requestError?.message || 'Unable to load Salesforce account details.');
    } finally {
      setIsLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  const websiteUrl = useMemo(() => normalizeWebsiteUrl(account?.website), [account?.website]);
  const accountAutomationKey = useMemo(() => toAutomationKey(account?.id || account?.accountNumber || accountId), [account?.accountNumber, account?.id, accountId]);
  const salesforceUrl = useMemo(() => normalizeAbsoluteHttpUrl(account?.sfdcAccountUrl), [account?.sfdcAccountUrl]);

  const fullBillingAddress = useMemo(() => {
    if (account?.fullBillingAddress) return account.fullBillingAddress;

    return [
      account?.billingStreet,
      account?.billingCity,
      account?.billingState,
      account?.billingPostalCode,
      account?.billingCountry
    ].filter(Boolean).join(', ') || 'No billing address on file';
  }, [account]);

  if (isLoading) {
    return (
      <section className="account-details" data-testid="account-details-loading" aria-busy="true" aria-live="polite">
        <div className="card shadow-sm account-details__loading-card">
          <div className="card-body account-details__loading-body text-center">
            <div className="spinner-border text-tse-primary" role="status">
              <span className="visually-hidden">Loading</span>
            </div>
            <h1 className="account-details__loading-title">Loading account details</h1>
            <p className="account-details__loading-copy">Fetching the current Salesforce account record for this customer.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="account-details" data-testid="account-details-page">
      <PageHeader
        eyebrow="Salesforce Account"
        title="Account Details"
        description="Review the Salesforce customer profile, identifiers, and billing data returned by the TriServe Auth API."
        icon="person-vcard"
        iconTone="primary"
        actions={(
          <>
            <Link to="/accounts" className="btn btn-outline-secondary">
              <i className="bi bi-arrow-left me-2" aria-hidden="true"></i>
              Back to accounts
            </Link>
            <button
              type="button"
              className="btn btn-tse-primary"
              onClick={loadAccount}
              data-testid="account-details-refresh"
            >
              <i className="bi bi-arrow-repeat me-2" aria-hidden="true"></i>
              Refresh details
            </button>
          </>
        )}
      />

      {error ? (
        <InlineAlert
          variant="danger"
          icon="exclamation-octagon"
          title="Account details unavailable."
          message={` ${error}`}
          className="mb-0"
          dataTestId="account-details-error"
        >
          <div className="mt-3 d-flex gap-2 flex-wrap">
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={loadAccount}>
              Retry
            </button>
            <Link to="/accounts" className="btn btn-sm btn-outline-secondary">
              Return to accounts
            </Link>
          </div>
        </InlineAlert>
      ) : null}

      {!error && !account ? (
        <EmptyState
          icon="person-x"
          eyebrow="Accounts"
          title="Account not found"
          message="The Salesforce account response did not include a record for this ID."
          actions={(
            <Link to="/accounts" className="btn btn-outline-tse-primary">
              Back to accounts
            </Link>
          )}
          className="account-details__empty"
          testId="account-details-empty"
        />
      ) : null}

      {account ? (
        <div className="row g-3">
          <div className="col-12">
            <div className="card shadow-sm account-details__hero-card">
              <div className="card-body account-details__hero-body">
                <div>
                  <div className="account-details__eyebrow">Customer profile</div>
                  <h2 className="account-details__name" data-testid={`account-details-name-${accountAutomationKey}`}>
                    {formatValue(account.name)}
                  </h2>
                  <p className="account-details__summary mb-0" data-testid={`account-details-summary-${accountAutomationKey}`}>
                    {formatValue(account.type)} account in {formatValue(account.industry)}.
                  </p>
                </div>
                <div className="account-details__hero-actions">
                  <div className="account-details__pill-group">
                    <span className="account-details__pill" data-testid={`account-details-created-pill-${accountAutomationKey}`}>
                      Created {formatDateTime(account.createdDate)}
                    </span>
                    <span className="account-details__pill" data-testid={`account-details-tse-pill-${accountAutomationKey}`}>
                      TSE ID {formatValue(account.accountNumber)}
                    </span>
                  </div>
                  <div className="d-flex gap-2 flex-wrap justify-content-md-end">
                    {websiteUrl ? (
                      <a
                        href={websiteUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-outline-secondary"
                        data-testid={`account-details-link-website-${accountAutomationKey}`}
                        aria-label={`Open website for ${account?.name || 'account'}`}
                      >
                        <i className="bi bi-globe-americas me-2" aria-hidden="true"></i>
                        Open website
                      </a>
                    ) : null}
                    {salesforceUrl ? (
                      <a
                        href={salesforceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-outline-tse-primary"
                        data-testid="open-salesforce-account-link"
                        aria-label={`Open Salesforce record for ${account?.name || 'account'}`}
                      >
                        <i className="bi bi-lightning-charge-fill me-2" aria-hidden="true"></i>
                        Open in Salesforce
                      </a>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        data-testid="open-salesforce-account-link"
                        title="Salesforce link unavailable"
                        aria-label="Salesforce record link unavailable"
                        disabled
                      >
                        <i className="bi bi-lightning-charge-fill me-2" aria-hidden="true"></i>
                        Open in Salesforce
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-xl-6">
            <div className="card shadow-sm account-details__card h-100">
              <div className="card-body">
                <h3 className="account-details__card-title">System identifiers</h3>
                <dl className="account-details__list">
                  <div className="account-details__list-row">
                    <dt>TSE Account ID</dt>
                    <dd className="account-details__mono" data-testid={`account-details-field-tse-id-${accountAutomationKey}`}>
                      {formatValue(account.accountNumber)}
                    </dd>
                  </div>
                  <div className="account-details__list-row">
                    <dt>SFDC ID</dt>
                    <dd className="account-details__mono" data-testid={`account-details-field-sfdc-id-${accountAutomationKey}`}>
                      {formatValue(account.id)}
                    </dd>
                  </div>
                  <div className="account-details__list-row">
                    <dt>Created</dt>
                    <dd data-testid={`account-details-field-created-${accountAutomationKey}`}>{formatDateTime(account.createdDate)}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>

          <div className="col-12 col-xl-6">
            <div className="card shadow-sm account-details__card h-100">
              <div className="card-body">
                <h3 className="account-details__card-title">Account profile</h3>
                <dl className="account-details__list">
                  <div className="account-details__list-row">
                    <dt>Type</dt>
                    <dd data-testid={`account-details-field-type-${accountAutomationKey}`}>{formatValue(account.type)}</dd>
                  </div>
                  <div className="account-details__list-row">
                    <dt>Industry</dt>
                    <dd data-testid={`account-details-field-industry-${accountAutomationKey}`}>{formatValue(account.industry)}</dd>
                  </div>
                  <div className="account-details__list-row">
                    <dt>Phone</dt>
                    <dd>
                      {account?.phone ? (
                        <a
                          href={`tel:${account.phone}`}
                          className="account-details__link"
                          data-testid={`account-details-link-phone-${accountAutomationKey}`}
                          aria-label={`Call ${account?.name || 'account'}`}
                        >
                          {account.phone}
                        </a>
                      ) : <span data-testid={`account-details-field-phone-${accountAutomationKey}`}>Unavailable</span>}
                    </dd>
                  </div>
                  <div className="account-details__list-row">
                    <dt>Website</dt>
                    <dd>
                      {websiteUrl ? (
                        <a
                          href={websiteUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="account-details__link"
                          data-testid={`account-details-field-website-${accountAutomationKey}`}
                          aria-label={`Open website record for ${account?.name || 'account'}`}
                        >
                          {account.website}
                        </a>
                      ) : <span data-testid={`account-details-field-website-${accountAutomationKey}`}>Unavailable</span>}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="card shadow-sm account-details__card">
              <div className="card-body">
                <h3 className="account-details__card-title">Billing address</h3>
                <dl className="account-details__list account-details__list--grid">
                  <div className="account-details__list-row">
                    <dt>Street</dt>
                    <dd data-testid={`account-details-field-billing-street-${accountAutomationKey}`}>{formatValue(account.billingStreet)}</dd>
                  </div>
                  <div className="account-details__list-row">
                    <dt>City</dt>
                    <dd data-testid={`account-details-field-billing-city-${accountAutomationKey}`}>{formatValue(account.billingCity)}</dd>
                  </div>
                  <div className="account-details__list-row">
                    <dt>State</dt>
                    <dd data-testid={`account-details-field-billing-state-${accountAutomationKey}`}>{formatValue(account.billingState)}</dd>
                  </div>
                  <div className="account-details__list-row">
                    <dt>Postal code</dt>
                    <dd data-testid={`account-details-field-billing-postal-${accountAutomationKey}`}>{formatValue(account.billingPostalCode)}</dd>
                  </div>
                  <div className="account-details__list-row">
                    <dt>Country</dt>
                    <dd data-testid={`account-details-field-billing-country-${accountAutomationKey}`}>{formatValue(account.billingCountry)}</dd>
                  </div>
                  <div className="account-details__list-row account-details__list-row--full">
                    <dt>Full billing address</dt>
                    <dd data-testid={`account-details-field-billing-full-${accountAutomationKey}`}>{fullBillingAddress}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default AccountDetails;
